import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import moment from 'moment';
import { IDatepickerConfig, IDatepickerValue, IWeekPeriod, IDatePeriod } from '../types';
import { isEqual } from 'lodash';

@Component({
  selector: 'xux-datepicker-periods-week',
  templateUrl: './xux-datepicker-periods-week.component.html',
  styleUrls: ['./xux-datepicker-periods-week.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxDatepickerPeriodsWeekComponent implements OnInit, AfterViewInit {
  @Input() public config: IDatepickerConfig;
  @Input('weekPeriods') public _weekPeriods: IWeekPeriod[] = [];
  @Input('calendarValue') set _calendarValue(_value: IDatepickerValue) {
    this.calendarValue = _value  ? _value.value : null;
    if (!this._weekPeriods || !this._weekPeriods.length) {
      return;
    }
    this.setActivePeriod(this.calendarValue);
  }
  public value: IWeekPeriod = {
    year: null,
    period: null,
    week: null,
    dateStart: null,
    dateEnd: null,
  };
  public currentPeriod: IWeekPeriod = {
    year: null,
    period: null,
    week: null,
    dateStart: null,
    dateEnd: null,
  };

  public get years(): number[] {
    return this._weekPeriods.reduce((accumulator, period: IWeekPeriod) => {
      if (!accumulator.includes(period.year)) {
        accumulator.push(period.year);
      }
      return accumulator;
    }, []).sort((a, b) => b - a);
  }

  public get periods(): number[] {
    return this._weekPeriods.reduce((accumulator, period: IWeekPeriod) => {
      if (period.year === this.value.year && !accumulator.includes(period.period)) {
        accumulator.push(period.period);
      }
      return accumulator;
    }, []).sort((a, b) => a - b);
  }

  public get weeks(): IWeekPeriod[] {
    return this._weekPeriods.reduce((accumulator: IWeekPeriod[], period: IWeekPeriod) => {
      if (period.year === this.value.year && period.period === this.value.period) {
        accumulator.push(period);
      }
      return accumulator;
    }, []);
  }

  @Output() public onChange: EventEmitter<IDatePeriod> = new EventEmitter();
  private calendarValue: IDatePeriod;

  constructor() {}

  public ngOnInit(): void {
    if (this.calendarValue) {
      this.setActivePeriod(this.calendarValue);
    } else {
      this.selectCurrentPeriod();
    }
  }

  public ngAfterViewInit(): void {
    this.focusSelectedPeriod('active');
  }

  public selectYear(year: number): void {
    this.value.year = year;
    this.selectPeriod(this.periods[0]);
  }

  public selectPeriod(period: number): void {
    this.value.period = period;
  }

  public selectWeek(week: IWeekPeriod, emitValue: boolean = false): void {
    this.value.week = week.week;
    this.value.dateStart = week.dateStart;
    this.value.dateEnd = week.dateEnd;

    if (emitValue) {
      const dateStart = moment(week.dateStart).toDate();
      const dateEnd = moment(week.dateEnd).toDate();
      this.onChange.emit({ dateStart, dateEnd });
    }
  }

  public formatDate(date: string, format: string, subtract: number = 0): string {
    if (subtract) {
      return date ? moment(date).subtract(subtract, 'd').format(format) : '';
    } else {
      return date ? moment(date).format(format) : '';
    }
  }

  public prepareLabel(weekPeriod: IWeekPeriod): string {
    return weekPeriod.week ? `W${weekPeriod.week}` : `P${weekPeriod.period}`;
  }

  public compareWeek(week1: IWeekPeriod, week2: IWeekPeriod): boolean {
    return isEqual(week1, week2);
  }

  private setActivePeriod(period?: IDatePeriod): void {
    this.setCurrentPeriod();
    if (!period) {
      return;
    }
    const periodValue = this._weekPeriods.find((periodItem) => {
      return moment(periodItem.dateStart).isSame(period.dateStart, 'day') &&
          moment(periodItem.dateEnd).isSame(period.dateEnd, 'day');
    });
    if (periodValue) {
      this.selectYear(periodValue.year);
      this.selectPeriod(periodValue.period);
      this.selectWeek(periodValue);
    } else {
      this.selectCurrentPeriod();
    }
  }

  private focusSelectedPeriod(className: string): void {
    const periodsList: HTMLCollection = document.getElementsByClassName('date-period-period');
    const elementsArray: HTMLElement[] = [].slice.call(periodsList);
    for (const element of elementsArray) {
      if (element.className.indexOf(className) !== -1) {
        element.scrollIntoView({
          block: 'center',
        });
      }
    }
  }

  private setCurrentPeriod(): void {
    const period = this.getCurrentPeriod();
    if (!period) {
      return;
    }
    this.currentPeriod = period;
  }

  private selectCurrentPeriod(): void {
    this.setCurrentPeriod();
    if (this.currentPeriod.year) {
      this.selectYear(this.currentPeriod.year);
      this.selectPeriod(this.currentPeriod.period);
    } else {
      this.selectYear(this.years[0]);
    }
  }

  private getCurrentPeriod(): IWeekPeriod {
    const currentDate =  moment();
    return this._weekPeriods.find((period: IWeekPeriod) => {
      const dateStart = moment(period.dateStart);
      const dateEnd = moment(period.dateEnd);
      return currentDate.isBetween(dateStart, dateEnd, 'day', '[]') && !!period.week;
    });
  }
}
